import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-4" }
const _hoisted_2 = { class: "bg-accent p-3" }
const _hoisted_3 = { class: "whitespace-pre-wrap" }
const _hoisted_4 = {
  key: 0,
  class: "text-sm text-red-500"
}
const _hoisted_5 = { class: "flex flex-col gap-4" }
const _hoisted_6 = { class: "flex items-center justify-between text-xs text-muted" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "flex items-center gap-3 text-xs" }
const _hoisted_9 = ["onChange"]
const _hoisted_10 = ["value"]
const _hoisted_11 = ["onClick"]
const _hoisted_12 = { class: "flex flex-col gap-3 text-sm" }
const _hoisted_13 = { class: "flex flex-col divide-y divide-accent" }
const _hoisted_14 = { class: "flex items-center gap-2 min-w-32" }
const _hoisted_15 = ["onUpdate:modelValue"]
const _hoisted_16 = { class: "capitalize" }
const _hoisted_17 = { class: "flex-grow min-w-0 text-xs" }
const _hoisted_18 = ["href"]
const _hoisted_19 = {
  key: 1,
  class: "text-red-500 block truncate"
}
const _hoisted_20 = {
  key: 2,
  class: "text-muted block"
}
const _hoisted_21 = ["disabled", "onClick"]
const _hoisted_22 = ["disabled", "onClick"]
const _hoisted_23 = {
  key: 2,
  type: "button",
  disabled: "",
  class: "text-xs bg-accent text-muted px-3 py-1 opacity-60"
}
const _hoisted_24 = ["disabled", "onClick"]
const _hoisted_25 = { class: "flex items-center gap-3" }
const _hoisted_26 = ["disabled"]
const _hoisted_27 = ["disabled"]
const _hoisted_28 = {
  key: 0,
  class: "text-sm text-red-500"
}
const _hoisted_29 = {
  key: 1,
  class: "text-sm text-muted"
}

import type { DevRoeAma, DevRoeEntity, ComAtprotoRepoStrongRef } from '#shared/lex'

interface EntityEntry {
  rkey: string
  uri: string
  cid: string
  value: DevRoeEntity.Record
}

type AmaPlatform = 'bluesky' | 'mastodon' | 'linkedin' | 'youtubeShorts'
interface PlatformState {
  status: 'idle' | 'publishing' | 'success' | 'failed'
  url?: string
  error?: string
}

const BLUESKY_LIMIT = 300

interface DraftPost {
  text: string
  mentions: ComAtprotoRepoStrongRef.Main[]
}

interface PendingImage {
  blob: Blob
  width: number
  height: number
  backgroundStyleId: string
}
interface PendingVideo {
  blob: Blob
  width: number
  height: number
  durationSeconds: number
}
type PostEditorRef = {
  insertMention: (entity: EntityEntry) => void
  insertLink: (label: string, url: string) => void
}

export default /*@__PURE__*/_defineComponent({
  __name: 'AmaAnswer',
  props: {
    rkey: {},
    question: {},
    createdAt: {},
    initialPosts: {},
    initialPlatforms: {},
    initialPublishedLinks: {},
    initialImagePreviewUrl: {},
    initialImageDimensions: {},
    initialBackgroundStyleId: {}
  },
  setup(__props: any) {

const ALL_PLATFORMS: AmaPlatform[] = ['bluesky', 'mastodon', 'linkedin', 'youtubeShorts']

const props = __props

const FOOTER_PREVIEW_LENGTH = '\n\nroe.dev/ama\n\n#ama'.length
const { data: entities } = useAdminFetch<EntityEntry[]>('/api/admin/entities', { default: () => [] })

const posts = ref<DraftPost[]>(
  (props.initialPosts ?? [{ text: '' }]).map(p => ({
    text: p.text,
    mentions: (p.mentions ?? []) as ComAtprotoRepoStrongRef.Main[],
  })),
)
if (!posts.value.length) posts.value = [{ text: '', mentions: [] }]

const platforms = reactive({
  bluesky: props.initialPlatforms?.bluesky ?? true,
  mastodon: props.initialPlatforms?.mastodon ?? true,
  linkedin: props.initialPlatforms?.linkedin ?? true,
  youtubeShorts: props.initialPlatforms?.youtubeShorts ?? false,
})

// Seed per-platform state from the existing record so a returning editor
// sees "already published" rather than being prompted to re-post.
const platformState = reactive<Record<AmaPlatform, PlatformState>>(
  Object.fromEntries(ALL_PLATFORMS.map(p => {
    const url = props.initialPublishedLinks?.[p]
    return [p, url ? { status: 'success', url } : { status: 'idle' }]
  })) as Record<AmaPlatform, PlatformState>,
)

// Generated media, held in memory until publish. Images upload to the PDS
// at publish time (unreferenced PDS blobs are GC'd); video goes straight
// to YouTube and isn't persisted on the PDS.
const pendingImage = ref<PendingImage | null>(null)
const pendingVideo = ref<PendingVideo | null>(null)
const imageRemoved = ref(false)

function onImageGenerated (image: PendingImage) {
  pendingImage.value = image
  imageRemoved.value = false
}

function onImageRemoved () {
  pendingImage.value = null
  imageRemoved.value = true
}

// Plain-text answer for the video generator + YouTube description, with
// entity mention placeholders swapped for bare names.
const plainAnswerForVideo = computed(() => posts.value
  .map(p => p.text
    .replace(/@([a-z0-9]{13})\b/g, (_match, rkey) => {
      const ent = (entities.value ?? []).find(e => e.rkey === rkey)
      return ent ? `@${ent.value.name}` : ''
    })
    .replace(/  +/g, ' '))
  .filter(Boolean)
  .join('\n')
  .trim(),
)

const error = ref<string | null>(null)
const submitting = computed(() => ALL_PLATFORMS.some(p => platformState[p].status === 'publishing'))

const saving = ref(false)
const savedAt = ref<string | null>(null)
const saveError = ref<string | null>(null)

function addPost () {
  posts.value.push({ text: '', mentions: [] })
}

function removePost (i: number) {
  if (posts.value.length === 1) return
  posts.value.splice(i, 1)
}

// Recompute the strong-ref `mentions` array from the `@<rkey>`
// placeholders in `text`. Skipped entries silently drop mentions of
// entities that have since been deleted.
function syncMentions (post: DraftPost) {
  const ids = new Set<string>()
  for (const match of post.text.matchAll(/@([a-z0-9]{13})\b/g)) {
    ids.add(match[1]!)
  }
  post.mentions = Array.from(ids).flatMap(rkey => {
    const ent = (entities.value ?? []).find(e => e.rkey === rkey)
    return ent ? [{ uri: ent.uri, cid: ent.cid }] : []
  })
}

const editorRefs = ref<Array<PostEditorRef | null>>([])
function setEditorRef (i: number, el: unknown) {
  editorRefs.value[i] = el as PostEditorRef | null
}

function promptInsertLink (i: number) {
  const editor = editorRefs.value[i]
  if (!editor) return
  const label = window.prompt('Link text:')?.trim()
  if (!label) return
  const url = window.prompt('URL:')?.trim()
  if (!url) return
  editor.insertLink(label, url)
}

function onPickMention (i: number, target: HTMLSelectElement) {
  const ent = (entities.value ?? []).find(e => e.rkey === target.value)
  if (ent) editorRefs.value[i]?.insertMention(ent)
  target.value = ''
}

// Live Bluesky-equivalent character count, swapping each `@<rkey>`
// placeholder for a `@<handle>` estimate.
function blueskyLength (post: DraftPost, includeFooter: boolean): number {
  let len = post.text.length
  for (const match of post.text.matchAll(/@([a-z0-9]{13})\b/g)) {
    const ent = (entities.value ?? []).find(e => e.rkey === match[1])
    const handle = ent?.value.socialHandles?.bluesky ?? ''
    len = len - match[0].length + (handle ? handle.length + 1 : 0)
  }
  if (includeFooter) len += FOOTER_PREVIEW_LENGTH
  return len
}

// Upload the pending image to the PDS lazily, caching both the BlobRef
// and the in-flight promise so parallel publishes share a single upload.
const uploadedImage = ref<unknown>(null)
let uploadingPromise: Promise<unknown> | null = null
async function ensureImageUploaded (): Promise<unknown> {
  if (uploadedImage.value) return uploadedImage.value
  if (!pendingImage.value) return undefined
  if (uploadingPromise) return uploadingPromise

  const blob = pendingImage.value.blob
  uploadingPromise = (async () => {
    const buf = new Uint8Array(await blob.arrayBuffer())
    const result = await $fetch<{ blob: unknown }>('/api/admin/blobs', {
      method: 'POST',
      body: buf,
      headers: { 'content-type': blob.type || 'image/png' },
    })
    uploadedImage.value = result.blob
    return result.blob
  })()
  try {
    return await uploadingPromise
  }
  finally {
    uploadingPromise = null
  }
}

// Reset the cached upload when the user regenerates the image, so the
// next publish doesn't reuse a stale BlobRef.
watch(pendingImage, () => {
  uploadedImage.value = null
  uploadingPromise = null
})

function requirePosts (): boolean {
  if (posts.value.some(p => p.text.trim())) return true
  error.value = 'At least one post must have content.'
  return false
}

async function buildPayload (): Promise<Record<string, unknown>> {
  const image = await ensureImageUploaded()
  const payload: Record<string, unknown> = {
    question: props.question,
    posts: posts.value
      .filter(p => p.text.trim())
      .map(p => ({ text: p.text, mentions: p.mentions })),
    platforms: { ...platforms },
  }
  if (image && pendingImage.value) {
    payload.image = image
    payload.imageDimensions = { width: pendingImage.value.width, height: pendingImage.value.height }
    payload.backgroundStyle = pendingImage.value.backgroundStyleId
  }
  else if (imageRemoved.value) {
    payload.image = null
    payload.imageDimensions = null
    payload.backgroundStyle = null
  }
  return payload
}

async function saveDraft () {
  if (saving.value) return
  saving.value = true
  saveError.value = null
  try {
    await $fetch(`/api/admin/ama/${props.rkey}`, { method: 'PATCH', body: await buildPayload() })
    savedAt.value = new Date().toISOString()
    imageRemoved.value = false
  }
  catch (err) {
    const data = (err as { data?: { message?: string } }).data
    saveError.value = data?.message || (err instanceof Error ? err.message : String(err))
  }
  finally {
    saving.value = false
  }
}

async function publishToPlatform (platform: AmaPlatform, force = false) {
  if (platformState[platform].status === 'publishing') return
  if (!requirePosts()) return
  error.value = null
  platformState[platform] = { status: 'publishing' }

  try {
    const baseBody = {
      ...(await buildPayload()),
      ...(force ? { force: true } : {}),
    }

    const res = platform === 'youtubeShorts'
      ? await publishYouTube(baseBody)
      : await $fetch<{ success: boolean, url: string }>(
          `/api/admin/ama/${props.rkey}/publish/${platform}`,
          { method: 'POST', body: baseBody },
        )
    platformState[platform] = { status: 'success', url: res.url }
  }
  catch (err) {
    const data = (err as { data?: { message?: string } }).data
    platformState[platform] = {
      status: 'failed',
      error: data?.message || (err instanceof Error ? err.message : String(err)),
    }
  }
}

async function publishYouTube (baseBody: object): Promise<{ success: boolean, url: string }> {
  if (!pendingVideo.value) throw new Error('Generate a video before publishing to YouTube Shorts.')
  const form = new FormData()
  form.append('payload', new Blob([JSON.stringify({ ...baseBody, answer: plainAnswerForVideo.value })], { type: 'application/json' }))
  form.append('video', pendingVideo.value.blob, 'ama.webm')
  return $fetch<{ success: boolean, url: string }>(
    `/api/admin/ama/${props.rkey}/publish/youtube-shorts`,
    { method: 'POST', body: form },
  )
}

async function publishAll () {
  if (!requirePosts()) return
  error.value = null
  // Skip platforms that are unchecked, already published this session, or
  // (YouTube only) lack a generated video.
  const todo = ALL_PLATFORMS.filter(p =>
    platforms[p]
    && platformState[p].status !== 'success'
    && !(p === 'youtubeShorts' && !pendingVideo.value),
  )
  await Promise.all(todo.map(p => publishToPlatform(p)))
}

return (_ctx: any,_cache: any) => {
  const _component_AdminPostEditor = _resolveComponent("AdminPostEditor")!
  const _component_AdminAmaImageGenerator = _resolveComponent("AdminAmaImageGenerator")!
  const _component_AdminAmaVideoGenerator = _resolveComponent("AdminAmaVideoGenerator")!
  const _component_NuxtLink = _resolveComponent("NuxtLink")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      _cache[3] || (_cache[3] = _createElementVNode("h2", { class: "text-sm text-muted mb-1" }, " Question ", -1)),
      _createElementVNode("p", _hoisted_3, _toDisplayString(__props.question), 1)
    ]),
    (_unref(error))
      ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_unref(error)), 1))
      : _createCommentVNode("", true),
    _createElementVNode("section", _hoisted_5, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(posts), (post, i) => {
        return (_openBlock(), _createElementBlock("div", {
          key: i,
          class: "flex flex-col gap-2"
        }, [
          _createElementVNode("header", _hoisted_6, [
            _createElementVNode("span", null, "Post " + _toDisplayString(i + 1) + _toDisplayString(i === _unref(posts).length - 1 ? ' (+ footer)' : ''), 1),
            (_unref(posts).length > 1)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "hover:text-red-500",
                  onClick: ($event: any) => (removePost(i))
                }, " Remove ", 8, _hoisted_7))
              : _createCommentVNode("", true)
          ]),
          _createVNode(_component_AdminPostEditor, {
            ref_for: true,
            ref: (el) => setEditorRef(i, el),
            "model-value": post.text,
            entities: _unref(entities) ?? [],
            placeholder: i === 0 ? 'Your response…' : 'Continued…',
            "onUpdate:modelValue": (v) => { post.text = v; syncMentions(post) }
          }, null, 8, ["model-value", "entities", "placeholder", "onUpdate:modelValue"]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("select", {
              class: "bg-accent px-2 py-1",
              onChange: (e) => onPickMention(i, e.target as HTMLSelectElement)
            }, [
              _cache[4] || (_cache[4] = _createElementVNode("option", { value: "" }, " Insert mention… ", -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(entities) ?? [], (ent) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: ent.rkey,
                  value: ent.rkey
                }, " @" + _toDisplayString(ent.value.name), 9, _hoisted_10))
              }), 128))
            ], 40, _hoisted_9),
            _createElementVNode("button", {
              type: "button",
              class: "bg-accent px-2 py-1 hover:bg-accent/70 text-muted",
              onClick: ($event: any) => (promptInsertLink(i))
            }, " Insert link ", 8, _hoisted_11),
            _createElementVNode("span", {
              class: _normalizeClass([{
              'text-red-500': blueskyLength(post, i === _unref(posts).length - 1) > BLUESKY_LIMIT,
              'text-yellow-500': blueskyLength(post, i === _unref(posts).length - 1) > BLUESKY_LIMIT - 30 && blueskyLength(post, i === _unref(posts).length - 1) <= BLUESKY_LIMIT,
            }, "text-muted"])
            }, _toDisplayString(blueskyLength(post, i === _unref(posts).length - 1)) + " / " + _toDisplayString(BLUESKY_LIMIT) + " (bluesky) ", 3)
          ])
        ]))
      }), 128)),
      _createElementVNode("button", {
        type: "button",
        class: "text-sm text-muted hover:text-primary self-start",
        onClick: addPost
      }, " + Add post ")
    ]),
    _createVNode(_component_AdminAmaImageGenerator, {
      question: __props.question,
      "created-at": __props.createdAt,
      "initial-preview-url": __props.initialImagePreviewUrl,
      "initial-background-style-id": __props.initialBackgroundStyleId,
      onGenerated: onImageGenerated,
      onRemoved: onImageRemoved,
      onCleared: _cache[0] || (_cache[0] = ($event: any) => (pendingImage.value = null))
    }, null, 8, ["question", "created-at", "initial-preview-url", "initial-background-style-id"]),
    (_unref(platforms).youtubeShorts)
      ? (_openBlock(), _createBlock(_component_AdminAmaVideoGenerator, {
          key: 1,
          question: __props.question,
          answer: _unref(plainAnswerForVideo),
          "background-style-id": _unref(pendingImage)?.backgroundStyleId ?? __props.initialBackgroundStyleId,
          onGenerated: _cache[1] || (_cache[1] = ($event: any) => (pendingVideo.value = $event)),
          onCleared: _cache[2] || (_cache[2] = ($event: any) => (pendingVideo.value = null))
        }, null, 8, ["question", "answer", "background-style-id"]))
      : _createCommentVNode("", true),
    _createElementVNode("section", _hoisted_12, [
      _cache[5] || (_cache[5] = _createElementVNode("h3", { class: "text-muted" }, " Publish ", -1)),
      _createElementVNode("ul", _hoisted_13, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(ALL_PLATFORMS, (platform) => {
          return _createElementVNode("li", {
            key: platform,
            class: "flex items-center gap-3 py-2"
          }, [
            _createElementVNode("label", _hoisted_14, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": ($event: any) => ((_unref(platforms)[platform]) = $event),
                type: "checkbox"
              }, null, 8, _hoisted_15), [
                [_vModelCheckbox, _unref(platforms)[platform]]
              ]),
              _createElementVNode("span", _hoisted_16, _toDisplayString(platform === 'youtubeShorts' ? 'YouTube Shorts' : platform), 1)
            ]),
            _createElementVNode("div", _hoisted_17, [
              (_unref(platformState)[platform].url)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    href: _unref(platformState)[platform].url,
                    target: "_blank",
                    rel: "noopener",
                    class: "text-muted hover:text-primary underline-offset-4 hover:underline truncate block"
                  }, _toDisplayString(_unref(platformState)[platform].url), 9, _hoisted_18))
                : _createCommentVNode("", true),
              (_unref(platformState)[platform].error)
                ? (_openBlock(), _createElementBlock("span", _hoisted_19, _toDisplayString(_unref(platformState)[platform].error), 1))
                : (platform === 'youtubeShorts' && _unref(platforms).youtubeShorts && !_unref(pendingVideo) && _unref(platformState)[platform].status !== 'success')
                  ? (_openBlock(), _createElementBlock("span", _hoisted_20, "Generate a video first."))
                  : _createCommentVNode("", true)
            ]),
            (_unref(platformState)[platform].status === 'success')
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  disabled: _unref(submitting) || (platform === 'youtubeShorts' && !_unref(pendingVideo)),
                  class: "text-xs text-muted hover:text-red-500 transition-colors disabled:opacity-60",
                  onClick: ($event: any) => (publishToPlatform(platform, true))
                }, " Re-publish ", 8, _hoisted_21))
              : (_unref(platformState)[platform].status === 'failed')
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    type: "button",
                    disabled: _unref(submitting) || !_unref(platforms)[platform] || (platform === 'youtubeShorts' && !_unref(pendingVideo)),
                    class: "text-xs bg-accent text-primary px-3 py-1 hover:bg-accent/70 transition-colors disabled:opacity-60",
                    onClick: ($event: any) => (publishToPlatform(platform))
                  }, " Retry ", 8, _hoisted_22))
                : (_unref(platformState)[platform].status === 'publishing')
                  ? (_openBlock(), _createElementBlock("button", _hoisted_23, " Publishing… "))
                  : (_openBlock(), _createElementBlock("button", {
                      key: 3,
                      type: "button",
                      disabled: _unref(submitting) || !_unref(platforms)[platform] || (platform === 'youtubeShorts' && !_unref(pendingVideo)),
                      class: "text-xs bg-primary text-background px-3 py-1 hover:bg-primary/90 transition-colors disabled:opacity-60",
                      onClick: ($event: any) => (publishToPlatform(platform))
                    }, " Publish ", 8, _hoisted_24))
          ])
        }), 64))
      ])
    ]),
    _createElementVNode("div", _hoisted_25, [
      _createElementVNode("button", {
        type: "button",
        disabled: _unref(submitting),
        class: "bg-primary text-background px-4 py-2 hover:bg-primary/90 transition-colors disabled:opacity-60",
        onClick: publishAll
      }, _toDisplayString(_unref(submitting) ? 'Publishing…' : 'Publish all enabled'), 9, _hoisted_26),
      _createElementVNode("button", {
        type: "button",
        disabled: _unref(saving),
        class: "bg-accent text-primary px-4 py-2 hover:bg-accent/70 transition-colors disabled:opacity-60",
        onClick: saveDraft
      }, _toDisplayString(_unref(saving) ? 'Saving…' : 'Save draft'), 9, _hoisted_27),
      (_unref(saveError))
        ? (_openBlock(), _createElementBlock("span", _hoisted_28, _toDisplayString(_unref(saveError)), 1))
        : (_unref(savedAt))
          ? (_openBlock(), _createElementBlock("span", _hoisted_29, "Draft saved"))
          : _createCommentVNode("", true),
      _createVNode(_component_NuxtLink, {
        to: "/admin/ama",
        class: "text-muted hover:text-primary self-center text-sm"
      }, {
        default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
          _createTextVNode(" Back ", -1)
        ]))]),
        _: 1
      })
    ])
  ]))
}
}

})